import type { ReactNode } from "react";
import { Modal } from "./Modal.tsx";
import i18n from "../util/i18n.ts";

// =============================================================================
// MobileDecisionDialog — the "决定" container of the mobile game-UI paradigm
// (GAME_PRODUCT_DESIGN_REVIEW §二 D8): a CENTERED modal with a single main
// action + a header X, distinct from the "工具" bottom sheet (MobileSheet) and
// the "时刻" full-screen ritual (momentOverlay). Reuses the shared Modal.tsx
// machinery (ModalManager / portal / focus trap, animation disabled) and
// Bootstrap's modal-dialog-centered; styled via the .mobile-decision-dialog*
// family (see light.scss), a sibling of the .mobile-title-context-modal* shell.
// -----------------------------------------------------------------------------
// BOUNDARY CONTRACT: the SAME boundary as MOBILE_SHEET_CONTRACT_V1 (MobileSheet.tsx):
//
// 1. This dialog NEVER enters the navigation back stack. It is pure component
//    state (an `open` boolean owned by the host). Opening/closing changes no URL
//    and pushes no history entry, so the global back control always acts on the
//    underlying page. This component mutates no history/pushState/location and
//    writes no m_return / m_src return-stack keys.
//
// 2. TWO exit paths only:
//      - close: `onClose()` dismisses the dialog in place — the header X and a
//        backdrop tap both call it. There is exactly ONE close-semantic control
//        (the X); the dialog never renders a second "close"/"return" button.
//      - navigate: the optional single `action` (a link/CTA) navigates away and
//        the dialog unmounts with the view. A cross-tab action must be seeded by
//        the CALLER via createMobileCrossTabHref — this component seeds nothing.
//
// 3. Direct-URL / deep-link landing is the host's concern, decided where the
//    dialog is mounted (see OwnerLetterDialog + LeagueDashboard), not here.
// =============================================================================

export const MobileDecisionDialog = ({
	action,
	children,
	onClose,
	open,
	title,
}: {
	// The single main action (a NAVIGATE CTA). Omit for a no-decision dialog whose
	// only control is the header X.
	action?: ReactNode;
	children: ReactNode;
	onClose: () => void;
	open: boolean;
	title: ReactNode;
}) => {
	return (
		<Modal
			aria-modal="true"
			className="mobile-decision-dialog"
			contentClassName="mobile-decision-dialog-content"
			data-mobile-decision-dialog=""
			dialogClassName="modal-dialog-centered mobile-decision-dialog-dialog"
			onHide={onClose}
			scrollable
			show={open}
		>
			<Modal.Header
				className="mobile-decision-dialog-header border-0"
				closeButton
				closeLabel={i18n.t("common.close", {
					ns: "ui",
					defaultValue: "Close",
				})}
			>
				<Modal.Title className="mobile-decision-dialog-title">
					{title}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="mobile-decision-dialog-body">{children}</Modal.Body>
			{action !== undefined ? (
				<Modal.Footer className="mobile-decision-dialog-footer">
					{action}
				</Modal.Footer>
			) : null}
		</Modal>
	);
};
